import { useMemo, useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Select,
  Tag,
  Button,
  Drawer,
  Form,
  InputNumber,
  TimePicker,
  Modal,
  message,
  Alert,
  Checkbox,
  Spin,
  Divider,
} from 'antd';
import { PlusOutlined, DeleteOutlined, CopyOutlined, EditOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useOperatorStore } from '../../store/useOperatorStore';
import WarningMessage from '../../components/WarningMessage';
import { useIsEditAllowedWithOperator } from '../../utils/utils';
import {
  useFetchTouPricingList,
  useFetchTouPricingDetail,
  useUpsertTouPricing,
  useCopyTouPricing,
  useDeleteTouPricing,
} from './Hooks';

const timeToDayjs = (hhmm) => {
  if (!hhmm) return null;
  if (hhmm === '24:00') return dayjs('23:59', 'HH:mm');
  return dayjs(hhmm, 'HH:mm');
};

const dayjsToTime = (val, isEnd) => {
  if (!val) return null;
  if (isEnd && val.format('HH:mm') === '23:59') return '24:00';
  return val.format('HH:mm');
};

const timeToMinutes = (hhmm) => {
  if (!hhmm) return null;
  if (hhmm === '24:00') return 24 * 60;
  const [h, m] = String(hhmm).split(':').map(Number);
  return h * 60 + m;
};

const minutesToTime = (mins) => {
  if (mins >= 24 * 60) return '24:00';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const getYearOptions = () => {
  const current = dayjs().year();
  const options = [];
  for (let y = current - 2; y <= current + 5; y++) {
    options.push({ label: String(y), value: y });
  }
  return options;
};

const daysInMonthFor = (year, month) =>
  dayjs(`${year}-${String(month).padStart(2, '0')}-01`).daysInMonth();

const buildPeriodsFromTariff = (tariff) =>
  (tariff.periods || []).map((p) => ({
    dateFrom: p.dateFrom,
    dateTo: p.dateTo,
    slots: (p.slots || []).map((s) => ({
      timeRange: [timeToDayjs(s.startTime), timeToDayjs(s.endTime)],
      rate: s.rate,
    })),
  }));

const buildDefaultPeriods = (year, month) => [
  {
    dateFrom: 1,
    dateTo: daysInMonthFor(year, month),
    slots: [
      {
        timeRange: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')],
        rate: 0,
      },
    ],
  },
];

const mapFormPeriods = (values) =>
  (values.periods || []).map((p) => ({
    dateFrom: p.dateFrom,
    dateTo: p.dateTo,
    slots: (p.slots || []).map((s) => ({
      startTime: dayjsToTime(s.timeRange?.[0], false),
      endTime: dayjsToTime(s.timeRange?.[1], true),
      rate: s.rate,
    })),
  }));

/** Live coverage analysis — separates incomplete fields from real coverage gaps. */
const analyzeCoverage = (periods, daysInMonth, t) => {
  const incomplete = [];
  const gaps = [];
  const periodIssues = {};
  const periodHasIncomplete = {};

  const addPeriodIssue = (idx, msg, kind) => {
    if (!periodIssues[idx]) periodIssues[idx] = [];
    if (!periodIssues[idx].includes(msg)) periodIssues[idx].push(msg);
    if (kind === 'incomplete') {
      periodHasIncomplete[idx] = true;
      if (!incomplete.includes(msg)) incomplete.push(msg);
    } else if (!gaps.includes(msg)) {
      gaps.push(msg);
    }
  };

  const addGlobalGap = (msg) => {
    if (!gaps.includes(msg)) gaps.push(msg);
  };

  if (!periods || !periods.length) {
    return {
      isComplete: false,
      status: 'incomplete',
      issues: [t('validation.periodRequired')],
      incomplete: [t('validation.periodRequired')],
      gaps: [],
      periodIssues: {},
      periodHasIncomplete: {},
    };
  }

  const dayPeriods = [];

  for (let i = 0; i < periods.length; i++) {
    const p = periods[i];
    const dateFrom = Number(p.dateFrom);
    const dateTo = Number(p.dateTo);

    if (!p.dateFrom || !p.dateTo) {
      addPeriodIssue(i, t('validation.dateRequired', { n: i + 1 }), 'incomplete');
      continue;
    }
    if (dateFrom < 1 || dateTo < 1) {
      addPeriodIssue(i, t('validation.dayMin', { n: i + 1 }), 'gap');
      continue;
    }
    if (dateFrom > daysInMonth || dateTo > daysInMonth) {
      addPeriodIssue(i, t('validation.dayMax', { n: i + 1, max: daysInMonth }), 'gap');
      continue;
    }
    if (dateFrom > dateTo) {
      addPeriodIssue(i, t('validation.dateOrder', { n: i + 1 }), 'gap');
      continue;
    }
    if (!p.slots || !p.slots.length) {
      addPeriodIssue(i, t('validation.slotRequired', { n: i + 1 }), 'incomplete');
      continue;
    }

    const slots = [];
    let slotsOk = true;
    for (let j = 0; j < p.slots.length; j++) {
      const s = p.slots[j];
      const startTime = dayjsToTime(s.timeRange?.[0], false);
      const endTime = dayjsToTime(s.timeRange?.[1], true);
      const hasTime = !!(startTime && endTime);
      const hasRate = !(s.rate === undefined || s.rate === null || Number(s.rate) < 0);

      if (!hasTime) {
        addPeriodIssue(i, t('validation.emptySlot', { n: i + 1, s: j + 1 }), 'incomplete');
        slotsOk = false;
        continue;
      }
      if (!hasRate) {
        addPeriodIssue(i, t('validation.rateRequired', { n: i + 1, s: j + 1 }), 'incomplete');
        slotsOk = false;
        continue;
      }
      const start = timeToMinutes(startTime);
      const end = timeToMinutes(endTime);
      if (end <= start) {
        addPeriodIssue(i, t('validation.timeOrder', { n: i + 1, s: j + 1 }), 'gap');
        slotsOk = false;
        continue;
      }
      slots.push({ start, end, startTime, endTime });
    }

    if (!slotsOk || !slots.length) continue;

    slots.sort((a, b) => a.start - b.start);
    let cursor = 0;
    let slotCoverageOk = true;
    for (let j = 0; j < slots.length; j++) {
      const slot = slots[j];
      if (slot.start < cursor) {
        addPeriodIssue(
          i,
          t('validation.slotOverlapDetail', {
            n: i + 1,
            start: slot.startTime,
            end: minutesToTime(cursor),
          }),
          'gap',
        );
        slotCoverageOk = false;
      } else if (slot.start > cursor) {
        addPeriodIssue(
          i,
          t('validation.slotGapDetail', {
            n: i + 1,
            start: minutesToTime(cursor),
            end: slot.startTime,
          }),
          'gap',
        );
        slotCoverageOk = false;
      }
      cursor = Math.max(cursor, slot.end);
    }
    if (cursor < 24 * 60) {
      addPeriodIssue(
        i,
        t('validation.slotIncompleteDetail', {
          n: i + 1,
          start: minutesToTime(cursor),
          end: '24:00',
        }),
        'gap',
      );
      slotCoverageOk = false;
    }

    if (slotCoverageOk) {
      dayPeriods.push({ dateFrom, dateTo, idx: i });
    }
  }

  // Day coverage only when every period's slots are filled (no empty rows)
  if (incomplete.length === 0) {
    if (!dayPeriods.length) {
      // slot gaps already recorded on periods
    } else {
      const sorted = [...dayPeriods].sort((a, b) => a.dateFrom - b.dateFrom);

      if (sorted[0].dateFrom > 1) {
        addGlobalGap(
          t('validation.dayGapDetail', {
            from: 1,
            to: sorted[0].dateFrom - 1,
          }),
        );
      }

      let dayCursor = 0;
      for (let i = 0; i < sorted.length; i++) {
        const p = sorted[i];
        if (dayCursor > 0 && p.dateFrom <= dayCursor) {
          addPeriodIssue(
            p.idx,
            t('validation.periodOverlapDetail', {
              from: p.dateFrom,
              to: Math.min(p.dateTo, dayCursor),
            }),
            'gap',
          );
        } else if (dayCursor > 0 && p.dateFrom > dayCursor + 1) {
          addGlobalGap(
            t('validation.dayGapDetail', {
              from: dayCursor + 1,
              to: p.dateFrom - 1,
            }),
          );
        }
        dayCursor = Math.max(dayCursor, p.dateTo);
      }

      if (dayCursor < daysInMonth) {
        addGlobalGap(
          t('validation.dayGapDetail', {
            from: dayCursor + 1,
            to: daysInMonth,
          }),
        );
      }
    }
  }

  const status =
    incomplete.length > 0 ? 'incomplete' : gaps.length > 0 ? 'gaps' : 'complete';

  return {
    isComplete: status === 'complete',
    status,
    issues: [...incomplete, ...gaps],
    incomplete,
    gaps,
    periodIssues,
    periodHasIncomplete,
  };
};

/** True if a period's filled slots already cover 00:00–24:00. */
const periodSlotsCoverFullDay = (period) => {
  const slots = [];
  for (const s of period?.slots || []) {
    const startTime = dayjsToTime(s.timeRange?.[0], false);
    const endTime = dayjsToTime(s.timeRange?.[1], true);
    if (!startTime || !endTime) continue;
    const start = timeToMinutes(startTime);
    const end = timeToMinutes(endTime);
    if (end > start) slots.push({ start, end });
  }
  if (!slots.length) return false;
  slots.sort((a, b) => a.start - b.start);
  let cursor = 0;
  for (const slot of slots) {
    if (slot.start > cursor) return false;
    cursor = Math.max(cursor, slot.end);
  }
  return cursor >= 24 * 60;
};

const renderStatusTag = (m, t) => {
  if (m.status === 'COMPLETE') return <Tag color="green">{t('complete')}</Tag>;
  if (m.status === 'HAS_GAPS') return <Tag color="orange">{t('hasGaps')}</Tag>;
  return <Tag>{t('notSet')}</Tag>;
};

const TouPricingPage = () => {
  const { t } = useTranslation('touPricing');
  const { operatorId } = useOperatorStore();
  const isEditAllowed = useIsEditAllowedWithOperator();
  const [year, setYear] = useState(dayjs().year());
  const [editMonth, setEditMonth] = useState(null);
  const [copyMonth, setCopyMonth] = useState(null);
  const [copyTargets, setCopyTargets] = useState([]);
  const [form] = Form.useForm();
  const watchedPeriods = Form.useWatch('periods', form);

  const { data, isLoading, refetch } = useFetchTouPricingList(operatorId, year);
  const { data: detailData, isLoading: detailLoading } = useFetchTouPricingDetail(
    operatorId,
    year,
    editMonth,
    !!editMonth,
  );
  const upsertMutation = useUpsertTouPricing();
  const copyMutation = useCopyTouPricing();
  const deleteMutation = useDeleteTouPricing();

  const months = data?.months || [];
  const yearOptions = useMemo(() => getYearOptions(), []);
  const daysInMonth = editMonth ? daysInMonthFor(year, editMonth) : 31;

  const coverage = useMemo(
    () => analyzeCoverage(watchedPeriods, daysInMonth, t),
    [watchedPeriods, daysInMonth, t],
  );

  useEffect(() => {
    if (!editMonth) return;
    if (detailData?.tariff) {
      form.setFieldsValue({ periods: buildPeriodsFromTariff(detailData.tariff) });
    } else {
      form.setFieldsValue({ periods: buildDefaultPeriods(year, editMonth) });
    }
  }, [detailData, editMonth, form, year]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const live = analyzeCoverage(values.periods, daysInMonth, t);
      if (!live.isComplete) {
        message.error(live.issues[0] || t('coverageGaps'));
        return;
      }

      await upsertMutation.mutateAsync({
        operatorId,
        year,
        month: editMonth,
        periods: mapFormPeriods(values),
        status: 'ACTIVE',
      });
      message.success(t('saveSuccess'));
      setEditMonth(null);
      refetch();
    } catch (err) {
      if (err?.errorFields) {
        message.error(t('validation.formInvalid'));
        return;
      }
      message.error(err?.response?.data?.message || err?.message || 'Error');
    }
  };

  const handleCopy = async () => {
    try {
      if (!copyTargets.length) {
        message.warning(t('validation.copyTargets'));
        return;
      }
      const targets = [];
      for (const m of copyTargets) {
        targets.push({ year, month: m });
      }
      await copyMutation.mutateAsync({
        operatorId,
        sourceYear: year,
        sourceMonth: copyMonth,
        targets,
      });
      message.success(t('copySuccess'));
      setCopyMonth(null);
      setCopyTargets([]);
      refetch();
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || 'Error');
    }
  };

  const confirmDelete = async (tariffId) => {
    await deleteMutation.mutateAsync(tariffId);
    message.success(t('deleteSuccess'));
    refetch();
  };

  const handleDelete = (monthItem) => {
    if (!monthItem.tariffId) return;
    Modal.confirm({
      title: t('deleteConfirm'),
      onOk: () => confirmDelete(monthItem.tariffId),
    });
  };

  if (!operatorId) {
    return (
      <div className="p-4">
        <WarningMessage />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold m-0">{t('title')}</h2>
        <Select
          value={year}
          onChange={setYear}
          options={yearOptions}
          style={{ width: 120 }}
          placeholder={t('year')}
          showSearch
          optionFilterProp="label"
        />
      </div>

      <Spin spinning={isLoading}>
        <Row gutter={[16, 16]}>
          {months.map((m) => (
            <Col xs={24} sm={12} md={8} lg={6} key={m.month}>
              <Card
                size="small"
                title={t(`months.${m.month}`)}
                extra={renderStatusTag(m, t)}
                actions={
                  isEditAllowed
                    ? [
                        <EditOutlined key="edit" onClick={() => setEditMonth(m.month)} />,
                        <CopyOutlined
                          key="copy"
                          onClick={() => {
                            if (!m.tariffId) {
                              message.warning(t('notSet'));
                              return;
                            }
                            setCopyMonth(m.month);
                            setCopyTargets([]);
                          }}
                        />,
                        <DeleteOutlined
                          key="delete"
                          onClick={() => handleDelete(m)}
                          style={{ color: m.tariffId ? undefined : '#ccc' }}
                        />,
                      ]
                    : []
                }
              >
                <div className="text-sm text-gray-600">
                  {t('periods')}: {m.periodCount || 0}
                </div>
                <div className="text-sm text-gray-600">
                  {t('slots')}: {m.slotCount || 0}
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </Spin>

      <Drawer
        title={`${t('edit')} — ${editMonth ? t(`months.${editMonth}`) : ''} ${year}`}
        open={!!editMonth}
        onClose={() => setEditMonth(null)}
        width={720}
        destroyOnClose
        styles={{ body: { padding: '20px 24px 24px' } }}
        extra={
          isEditAllowed ? (
            <Button type="primary" loading={upsertMutation.isPending} onClick={handleSave}>
              {t('save')}
            </Button>
          ) : null
        }
      >
        <Alert type="warning" showIcon className="mb-4" message={t('editWarning')} />
        <div className="mb-4 text-sm text-gray-500">{t('coverageHint', { days: daysInMonth })}</div>

        {!!watchedPeriods && coverage.status === 'complete' && (
          <Alert type="success" showIcon className="mb-5" message={t('coverageOk')} />
        )}
        {!!watchedPeriods && coverage.status === 'incomplete' && (
          <Alert
            type="warning"
            showIcon
            className="mb-5"
            message={t('coverageIncomplete')}
            description={
              <ul className="mb-0 pl-4 list-disc">
                {coverage.incomplete.map((msg) => (
                  <li key={msg}>{msg}</li>
                ))}
              </ul>
            }
          />
        )}
        {!!watchedPeriods && coverage.status === 'gaps' && (
          <Alert
            type="error"
            showIcon
            className="mb-5"
            message={t('coverageGaps')}
            description={
              <ul className="mb-0 pl-4 list-disc">
                {coverage.gaps.map((msg) => (
                  <li key={msg}>{msg}</li>
                ))}
              </ul>
            }
          />
        )}

        <Spin spinning={detailLoading}>
          <Form form={form} layout="vertical" disabled={!isEditAllowed}>
            <Form.List name="periods">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name }) => {
                    const pIssues = coverage.periodIssues?.[name] || [];
                    const isIncomplete = !!coverage.periodHasIncomplete?.[name];
                    return (
                      <Card
                        key={key}
                        size="small"
                        className="mb-5"
                        styles={{ body: { padding: 16 } }}
                        title={
                          <span className="inline-flex items-center gap-2">
                            {`${t('periods')} #${name + 1}`}
                            {watchedPeriods?.[name] ? (
                              isIncomplete ? (
                                <Tag color="gold">{t('incomplete')}</Tag>
                              ) : pIssues.length ? (
                                <Tag color="orange">{t('hasGaps')}</Tag>
                              ) : (
                                <Tag color="green">{t('complete')}</Tag>
                              )
                            ) : null}
                          </span>
                        }
                        extra={
                          fields.length > 1 ? (
                            <Button
                              danger
                              type="text"
                              icon={<DeleteOutlined />}
                              onClick={() => remove(name)}
                              disabled={!isEditAllowed}
                            />
                          ) : null
                        }
                      >
                        {pIssues.length > 0 && !isIncomplete && (
                          <Alert
                            type="warning"
                            showIcon
                            className="mb-3"
                            message={
                              <ul className="mb-0 pl-4 list-disc text-sm">
                                {pIssues.map((msg) => (
                                  <li key={msg}>{msg}</li>
                                ))}
                              </ul>
                            }
                          />
                        )}
                        {isIncomplete && (
                          <Alert
                            type="info"
                            showIcon
                            className="mb-3"
                            message={t('validation.emptySlotHint')}
                          />
                        )}

                        <Row gutter={16}>
                          <Col span={12}>
                            <Form.Item
                              name={[name, 'dateFrom']}
                              label={t('dateFrom')}
                              rules={[
                                { required: true, message: t('validation.required') },
                                {
                                  validator: (_, value) => {
                                    if (value == null) return Promise.resolve();
                                    if (value < 1 || value > daysInMonth) {
                                      return Promise.reject(
                                        new Error(t('validation.dayRange', { max: daysInMonth })),
                                      );
                                    }
                                    return Promise.resolve();
                                  },
                                },
                              ]}
                            >
                              <InputNumber min={1} max={daysInMonth} style={{ width: '100%' }} />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              name={[name, 'dateTo']}
                              label={t('dateTo')}
                              dependencies={[['periods', name, 'dateFrom']]}
                              rules={[
                                { required: true, message: t('validation.required') },
                                {
                                  validator: (_, value) => {
                                    if (value == null) return Promise.resolve();
                                    if (value < 1 || value > daysInMonth) {
                                      return Promise.reject(
                                        new Error(t('validation.dayRange', { max: daysInMonth })),
                                      );
                                    }
                                    const from = form.getFieldValue(['periods', name, 'dateFrom']);
                                    if (from != null && value < from) {
                                      return Promise.reject(
                                        new Error(t('validation.dateOrderSimple')),
                                      );
                                    }
                                    return Promise.resolve();
                                  },
                                },
                              ]}
                            >
                              <InputNumber min={1} max={daysInMonth} style={{ width: '100%' }} />
                            </Form.Item>
                          </Col>
                        </Row>

                        <Divider orientation="left" plain className="!mt-2 !mb-4">
                          {t('slots')}
                        </Divider>

                        <Form.List name={[name, 'slots']}>
                          {(slotFields, slotOps) => (
                            <>
                              {slotFields.map((sf, slotIndex) => (
                                <Row key={sf.key} gutter={16} align="top" wrap={false}>
                                  <Col flex="auto" style={{ minWidth: 0 }}>
                                    <Form.Item
                                      name={[sf.name, 'timeRange']}
                                      label={slotIndex === 0 ? t('timeRange') : ' '}
                                      colon={slotIndex === 0}
                                      rules={[
                                        { required: true, message: t('validation.required') },
                                        {
                                          validator: (_, value) => {
                                            if (!value?.[0] || !value?.[1]) {
                                              return Promise.resolve();
                                            }
                                            const start = dayjsToTime(value[0], false);
                                            const end = dayjsToTime(value[1], true);
                                            if (timeToMinutes(end) <= timeToMinutes(start)) {
                                              return Promise.reject(
                                                new Error(t('validation.timeOrderSimple')),
                                              );
                                            }
                                            return Promise.resolve();
                                          },
                                        },
                                      ]}
                                    >
                                      <TimePicker.RangePicker
                                        format="HH:mm"
                                        style={{ width: '100%' }}
                                        order={false}
                                      />
                                    </Form.Item>
                                  </Col>
                                  <Col flex="130px">
                                    <Form.Item
                                      name={[sf.name, 'rate']}
                                      label={slotIndex === 0 ? t('rate') : ' '}
                                      colon={slotIndex === 0}
                                      rules={[
                                        { required: true, message: t('validation.required') },
                                        {
                                          type: 'number',
                                          min: 0,
                                          message: t('validation.rateMin'),
                                        },
                                      ]}
                                    >
                                      <InputNumber
                                        min={0}
                                        step={0.01}
                                        style={{ width: '100%' }}
                                      />
                                    </Form.Item>
                                  </Col>
                                  <Col flex="40px">
                                    <Form.Item
                                      label={slotIndex === 0 ? ' ' : ' '}
                                      colon={false}
                                    >
                                      {slotFields.length > 1 ? (
                                        <Button
                                          danger
                                          type="text"
                                          icon={<DeleteOutlined />}
                                          onClick={() => slotOps.remove(sf.name)}
                                          disabled={!isEditAllowed}
                                        />
                                      ) : (
                                        <span className="inline-block w-8" />
                                      )}
                                    </Form.Item>
                                  </Col>
                                </Row>
                              ))}
                              <Button
                                type="dashed"
                                block
                                icon={<PlusOutlined />}
                                className="mt-2 mb-0"
                                onClick={() => {
                                  const period = form.getFieldValue(['periods', name]);
                                  if (periodSlotsCoverFullDay(period)) {
                                    message.warning(t('validation.shortenBeforeAdd'));
                                  }
                                  slotOps.add({
                                    timeRange: undefined,
                                    rate: undefined,
                                  });
                                }}
                              >
                                {t('addSlot')}
                              </Button>
                            </>
                          )}
                        </Form.List>
                      </Card>
                    );
                  })}
                  <Button
                    type="dashed"
                    block
                    icon={<PlusOutlined />}
                    onClick={() =>
                      add({
                        dateFrom: undefined,
                        dateTo: undefined,
                        slots: [{ timeRange: undefined, rate: undefined }],
                      })
                    }
                  >
                    {t('addPeriod')}
                  </Button>
                </>
              )}
            </Form.List>
          </Form>
        </Spin>
      </Drawer>

      <Modal
        title={t('copyTitle')}
        open={!!copyMonth}
        onCancel={() => setCopyMonth(null)}
        onOk={handleCopy}
        confirmLoading={copyMutation.isPending}
      >
        <p>
          {t('copySource')}: {copyMonth ? t(`months.${copyMonth}`) : ''}
        </p>
        <Checkbox.Group
          value={copyTargets}
          onChange={setCopyTargets}
          options={Array.from({ length: 12 }, (_, i) => i + 1)
            .filter((m) => m !== copyMonth)
            .map((m) => ({ label: t(`months.${m}`), value: m }))}
          className="flex flex-col gap-1"
        />
      </Modal>
    </div>
  );
};

export default TouPricingPage;
